import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';

interface Artifact {
  id: string;
  docId: string;
  category: string;
  year: string;
  title: string;
  description: string;
  imageUrl: string;
  status: 'declassified' | 'classified' | 'restricted';
}

const Archive: React.FC = () => {
  const { t } = useTranslation();
  const [selectedCategory, setSelectedCategory] = useState<string>('all');

  const categories = [
    { id: 'all', label: t('archive.categories.all') },
    { id: 'architecture', label: t('archive.categories.architecture') },
    { id: 'design', label: t('archive.categories.design') },
    { id: 'industry', label: t('archive.categories.industry') },
    { id: 'culture', label: t('archive.categories.culture') },
    { id: 'sports', label: t('archive.categories.sports') },
  ];

  // Sample artifacts (you'll replace with actual data)
  const artifacts: Artifact[] = [
    {
      id: '1',
      docId: 'YU-ARC-1967-042',
      category: 'architecture',
      year: '1967',
      title: t('archive.artifacts.1.title'),
      description: t('archive.artifacts.1.description'),
      imageUrl: '/archive/artifact-1.jpg',
      status: 'declassified',
    },
    {
      id: '2',
      docId: 'YU-ARC-1974-088',
      category: 'design',
      year: '1974',
      title: t('archive.artifacts.2.title'),
      description: t('archive.artifacts.2.description'),
      imageUrl: '/archive/artifact-2.jpg',
      status: 'declassified',
    },
    {
      id: '3',
      docId: 'YU-ARC-1958-015',
      category: 'industry',
      year: '1958',
      title: t('archive.artifacts.3.title'),
      description: t('archive.artifacts.3.description'),
      imageUrl: '/archive/artifact-3.jpg',
      status: 'declassified',
    },
    {
      id: '4',
      docId: 'YU-ARC-1980-121',
      category: 'culture',
      year: '1980',
      title: t('archive.artifacts.4.title'),
      description: t('archive.artifacts.4.description'),
      imageUrl: '/archive/artifact-4.jpg',
      status: 'declassified',
    },
    {
      id: '5',
      docId: 'YU-ARC-1963-034',
      category: 'architecture',
      year: '1963',
      title: t('archive.artifacts.5.title'),
      description: t('archive.artifacts.5.description'),
      imageUrl: '/archive/artifact-5.jpg',
      status: 'declassified',
    },
    {
      id: '6',
      docId: 'YU-ARC-1971-099',
      category: 'sports',
      year: '1971',
      title: t('archive.artifacts.6.title'),
      description: t('archive.artifacts.6.description'),
      imageUrl: '/archive/artifact-6.jpg',
      status: 'declassified',
    },
  ];

  const filteredArtifacts =
    selectedCategory === 'all'
      ? artifacts
      : artifacts.filter(artifact => artifact.category === selectedCategory);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'declassified':
        return 'bg-green-600';
      case 'restricted':
        return 'bg-yellow-600';
      case 'classified':
        return 'bg-[#C41E3A]';
      default:
        return 'bg-gray-600';
    }
  };

  return (
    <>
      <Helmet>
        <title>Yugoslav Cultural Archive | Born in YU</title>
        <meta name="description" content="Explore the Yugoslav cultural archive featuring artifacts, documents, and historical items from the former Yugoslavia. Discover preserved heritage and cultural history." />
        <link rel="canonical" href="https://borninyu.com/archive" />
      </Helmet>
      <div className="min-h-screen bg-white">
        {/* Header */}
        <div className="border-b-4 border-black bg-white p-8 md:p-16">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-6">
            <div className="text-[10px] font-bold uppercase tracking-widest">
              {t('archive.securityClearance')}: LEVEL 0 - PUBLIC ACCESS
            </div>
            <div className="border-2 border-black px-3 py-1 text-[10px] font-black uppercase">
              {t('archive.totalArtifacts')}: {artifacts.length}
            </div>
          </div>

          <h1 className="text-5xl md:text-7xl font-black uppercase leading-none mb-4 tracking-tighter">
            {t('archive.title')}
          </h1>

          <p className="text-lg font-bold uppercase text-gray-700 max-w-3xl">
            {t('archive.subtitle')}
          </p>
        </div>
      </div>

      {/* Category Filter */}
      <div className="border-b-3 border-black bg-gray-100 p-4 md:p-6 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-wrap gap-3">
            {categories.map(category => (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                className={`px-4 py-2 text-xs font-black uppercase border-2 border-black transition-all ${
                  selectedCategory === category.id
                    ? 'bg-black text-white'
                    : 'bg-white text-black hover:bg-gray-200'
                }`}
              >
                {category.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Artifacts Grid */}
      <div className="max-w-7xl mx-auto p-8 md:p-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredArtifacts.map(artifact => (
            <div
              key={artifact.id}
              className="border-3 border-black bg-white hover:shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] transition-all"
            >
              {/* Image Placeholder */}
              <div className="aspect-[4/3] bg-gray-200 border-b-3 border-black relative overflow-hidden">
                {/* Placeholder pattern */}
                <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-gray-300 to-gray-400">
                  <div className="text-6xl font-black text-gray-500 opacity-20">
                    {artifact.year}
                  </div>
                </div>
                {/* Status Badge */}
                <div
                  className={`absolute top-3 right-3 ${getStatusColor(artifact.status)} text-white text-[8px] font-black uppercase px-2 py-1`}
                >
                  {t(`archive.status.${artifact.status}`)}
                </div>
              </div>

              {/* Content */}
              <div className="p-6">
                {/* Document ID */}
                <div className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-2">
                  {t('archive.docId')}: {artifact.docId}
                </div>

                {/* Title */}
                <h3 className="text-xl font-black uppercase leading-tight mb-3 tracking-tighter">
                  {artifact.title}
                </h3>

                {/* Description */}
                <p className="text-sm leading-relaxed mb-4 text-gray-700">{artifact.description}</p>

                {/* Meta Info */}
                <div className="flex items-center justify-between text-[10px] font-bold uppercase">
                  <div className="border border-black px-2 py-1">
                    {t('archive.year')}: {artifact.year}
                  </div>
                  <div className="border border-black px-2 py-1">
                    {t(`archive.categories.${artifact.category}`)}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* No Results */}
        {filteredArtifacts.length === 0 && (
          <div className="text-center py-16">
            <div className="text-4xl font-black uppercase text-gray-400 mb-4">
              {t('archive.noResults')}
            </div>
            <p className="text-sm uppercase font-bold text-gray-500">
              {t('archive.noResultsDescription')}
            </p>
          </div>
        )}
      </div>
    </div>
    </>
  );
};

export default Archive;
